// Pure data-shaping helpers for core/SelectDropdown. Options arrive in several shapes
// (DS `{value,label}`, legacy select2 `{id,text}`, bare strings/numbers, and
// optgroups as `{label,options}` or legacy `{text,children}`) — everything is
// normalised here so the component and react-select only ever see one shape.

const isGroup = (raw) =>
    raw != null &&
    typeof raw === 'object' &&
    (Array.isArray(raw.options) || Array.isArray(raw.children));

export const normalizeOption = (raw, group = null) => {
    if (raw == null) return null;
    if (typeof raw === 'string' || typeof raw === 'number') {
        return { value: raw, label: String(raw), disabled: false, group, raw };
    }
    const value = raw.value !== undefined ? raw.value : raw.id;
    const label = raw.label !== undefined ? raw.label : raw.text;
    if (value === undefined || label === undefined) return null;
    return {
        value,
        label,
        disabled: Boolean(raw.disabled),
        icon: raw.icon,
        description: raw.description,
        isNew: Boolean(raw.isNew),
        group,
        raw,
    };
};

// -> flat array of normalized options; group membership travels on `option.group`.
export const normalizeOptions = (options) => {
    const normalized = [];
    (options || []).forEach((raw) => {
        if (isGroup(raw)) {
            // '' or missing group labels mean "ungrouped" — never render an
            // empty group heading (e.g. phases without a group_name).
            const groupLabel = (raw.label !== undefined ? raw.label : raw.text) || null;
            (raw.options || raw.children).forEach((child) => {
                const option = normalizeOption(child, groupLabel);
                if (option) normalized.push(option);
            });
            return;
        }
        const option = normalizeOption(raw);
        if (option) normalized.push(option);
    });
    return normalized;
};

// Rebuild react-select's grouped shape ({label, options}) from the flat
// normalized list, preserving order. Ungrouped options stay top-level.
export const toGroupedOptions = (options) => {
    const grouped = [];
    let currentGroup = null;
    options.forEach((option) => {
        if (option.group == null) {
            currentGroup = null;
            grouped.push(option);
            return;
        }
        if (!currentGroup || currentGroup.label !== option.group) {
            currentGroup = { label: option.group, options: [] };
            grouped.push(currentGroup);
        }
        currentGroup.options.push(option);
    });
    return grouped;
};

// Value prop -> internal array shape (single values wrap into a one-item array).
// '' usually means "nothing selected", but some sources (dashboard widget
// filters) use { value: '', label: 'All …' } as a real option — so '' only
// counts as empty when no option claims it.
export const normalizeValue = (value, multiple, options = []) => {
    const hasEmptyOption = options.some((option) => option.value === '');
    if (value === undefined || value === null || (value === '' && !hasEmptyOption)) return [];
    if (multiple) return Array.isArray(value) ? value : [value];
    return [Array.isArray(value) ? value[0] : value];
};

// Loose equality on purpose: legacy select2 values round-trip through the DOM
// as strings, so 3 must match "3".
export const isSameValue = (a, b) => a === b || String(a) === String(b);

export const findOption = (options, value) =>
    options.find((option) => isSameValue(option.value, value)) || null;

// Resolve selected values to options; unknown values (async sources, tag
// initial values) fall back to label === value, same as the legacy pipeline.
export const resolveSelection = (values, options) =>
    values.map((value) => findOption(options, value) || { value, label: String(value), fallback: true, raw: { id: value, text: String(value) } });

// Case- and accent-insensitive comparison key. Finnish and Swedish name lists
// make this routine rather than exotic: without folding, "Jarvi" never finds
// "Järvi" and the user concludes the person is not in the list.
export const foldText = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

export const matchesTerm = (label, term) => {
    const query = foldText(term).trim();
    if (!query) return true;
    return foldText(label).includes(query);
};

// Whole-label equality, for deciding whether a typed tag already exists.
// Deliberately NOT matchesTerm: typing "De" must still be creatable as its own
// tag even when "Design" is in the list.
export const isSameLabel = (label, term) => foldText(label).trim() === foldText(term).trim();

// Legacy onChange payload: expose both key vocabularies on the data object so
// select2-era consumers (`data.id` / `data.text`) and new ones (`value`/`label`)
// both work. react-select's creatable marks created options with `__isNew__`
// and labels them with the formatted create-row text — the payload carries the
// raw term instead.
export const toOptionData = (option) => {
    const isNew = Boolean(option.isNew || option.__isNew__);
    const label = isNew ? String(option.value) : option.label;
    return {
        ...(option.raw && typeof option.raw === 'object' ? option.raw : {}),
        id: option.value,
        text: label,
        value: option.value,
        label,
        ...(isNew ? { isNew: true } : {}),
    };
};

// A portalled menu is positioned against the viewport, so scrolling an ancestor would leave it
// floating beside its control — react-select closes it. Scrolling the menu's own option list
// must not, hence the portal test rather than a blanket close.
export const shouldCloseMenuOnScroll = (event) => {
    const target = event?.target;
    if (target && typeof target.closest === 'function' && target.closest('.ds-select-dropdown__menu-portal')) {
        return false;
    }
    return true;
};
